/**
 * UsersView — owner-only view to create and list dashboard staff accounts.
 *
 * Roles:
 *   admin      — Insights + Operations + Management (no user management)
 *   manager    — Operations + Management only
 *   accountant — Books export only
 *
 * Both sign in with email + password at the dashboard URL.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveAppLocale } from '../locale.js';
import type { Database } from '@cafe-pos/supabase';
import { supabase } from '../supabase.js';
import { PageHeader } from '../ui/index.js';

type ProfileRow = Database['public']['Tables']['profiles']['Row'];

interface Credentials {
  email: string;
  password: string;
}

export function UsersView({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  const [users, setUsers] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // create form
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [newRole, setNewRole] = useState<'manager' | 'admin' | 'accountant'>('manager');
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState<Credentials | null>(null);
  const [copied, setCopied] = useState<'email' | 'password' | null>(null);

  // reset password
  const [resetId, setResetId] = useState<string | null>(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetCreds, setResetCreds] = useState<Credentials | null>(null);
  const [resetCopied, setResetCopied] = useState<'email' | 'password' | null>(null);

  // role change
  const [roleChanging, setRoleChanging] = useState<string | null>(null);

  async function load() {
    if (!supabase || !clientId) return;
    setLoading(true);
    const { data, error: err } = await supabase
      .from('profiles')
      .select('*')
      .in('role', ['admin', 'manager', 'accountant'])
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });
    if (err) setError(err.message);
    else setUsers(data ?? []);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  async function createUser() {
    if (!supabase) return;
    if (!email.trim()) {
      setError(t('users.emailRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    setCreds(null);

    const { data, error: err } = await supabase.functions.invoke('create-manager', {
      body: {
        email: email.trim(),
        fullName: fullName.trim() || undefined,
        role: newRole,
      },
    });

    setBusy(false);

    if (err) {
      let msg = (err as Error).message;
      try {
        const body = (await (err as unknown as { context: Response }).context.json()) as {
          error?: string;
        };
        if (body?.error) msg = body.error;
      } catch {
        /* use generic message */
      }
      setError(msg);
      return;
    }

    const result = data as { manager: { email: string; password: string } };
    setCreds(result.manager);
    setEmail('');
    setFullName('');
    void load();
  }

  function copy(text: string, field: 'email' | 'password') {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  async function resetPassword(userId: string) {
    if (!supabase) return;
    setResetId(userId);
    setResetBusy(true);
    setResetCreds(null);
    setResetCopied(null);
    const { data, error: err } = await supabase.functions.invoke('reset-manager-password', {
      body: { managerId: userId },
    });
    setResetBusy(false);
    if (err) {
      let msg = (err as Error).message;
      try {
        const body = (await (err as unknown as { context: Response }).context.json()) as {
          error?: string;
        };
        if (body?.error) msg = body.error;
      } catch {
        /* use generic */
      }
      setError(msg);
      setResetId(null);
      return;
    }
    const result = data as { email: string; password: string };
    setResetCreds(result);
  }

  function copyReset(text: string, field: 'email' | 'password') {
    void navigator.clipboard.writeText(text).then(() => {
      setResetCopied(field);
      setTimeout(() => setResetCopied(null), 2000);
    });
  }

  async function changeRole(userId: string, role: 'admin' | 'manager' | 'accountant') {
    if (!supabase) return;
    setRoleChanging(userId);
    const { error: err } = await supabase.functions.invoke('update-user-role', {
      body: { userId, role },
    });
    setRoleChanging(null);
    if (err) {
      let msg = (err as Error).message;
      try {
        const body = (await (err as unknown as { context: Response }).context.json()) as {
          error?: string;
        };
        if (body?.error) msg = body.error;
      } catch {
        /* use generic */
      }
      setError(msg);
      return;
    }
    void load();
  }

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('users.title')}
        subtitle={t('users.subtitle')}
      />

      {/* ── Role overview ─────────────────────────────────────────── */}
      <div className="info-box" style={{ marginBottom: 20 }}>
        <strong>{t('users.rolesTitle')}</strong>
        <ul style={{ marginTop: 6, paddingLeft: 18, fontSize: 13, lineHeight: 1.8 }}>
          <li>{t('users.roleAdmin')}</li>
          <li>{t('users.roleManager')}</li>
          <li>{t('users.roleAccountant')}</li>
        </ul>
        <p style={{ marginTop: 8, fontSize: 12 }}>
          {t('users.rolesFooter')}
        </p>{' '}
      </div>

      {/* ── Create form ───────────────────────────────────────────── */}
      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="panel-header">
          <span className="panel-title">{t('users.createTitle')}</span>
        </div>
        <div className="add-form">
          <input
            type="email"
            placeholder={t('users.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void createUser();
            }}
          />
          <input
            type="text"
            placeholder={t('users.namePlaceholder')}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void createUser();
            }}
          />
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as 'manager' | 'admin' | 'accountant')}
          >
            <option value="manager">{t('users.roleManagerOpt')}</option>
            <option value="admin">{t('users.roleAdminOpt')}</option>
            <option value="accountant">{t('users.roleAccountantOpt')}</option>
          </select>
          <button className="btn" onClick={() => void createUser()} disabled={busy}>
            {busy ? t('common.creating') : t('users.submit')}
          </button>
        </div>
        {error && (
          <p className="error-banner" style={{ margin: '0 14px 12px' }}>
            {error}
          </p>
        )}
      </div>

      {/* ── Generated credentials ─────────────────────────────────── */}
      {creds && (
        <div className="creds-box" style={{ marginBottom: 24 }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>{t('users.credsTitle')}</p>
          <p className="muted" style={{ marginBottom: 10, fontSize: 12 }}>
            {t('users.credsNote')}
          </p>
          <div className="creds-row">
            <span>{t('users.email')}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code>{creds.email}</code>
              <button className="btn-ghost btn-sm" onClick={() => copy(creds.email, 'email')}>
                {copied === 'email' ? t('users.copied') : t('common.copy')}
              </button>
            </div>
          </div>
          <div className="creds-row">
            <span>{t('users.password')}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code>{creds.password}</code>
              <button className="btn-ghost btn-sm" onClick={() => copy(creds.password, 'password')}>
                {copied === 'password' ? t('users.copied') : t('common.copy')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Users table ───────────────────────────────────────────── */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-title">
            {users.length === 0
              ? t('users.emptyTitle')
              : t('users.count', { count: users.length })}
          </span>
        </div>
        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : users.length === 0 ? (
          <div className="empty-state">{t('users.empty')}</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('users.colFullName')}</th>
                <th>{t('users.colEmail')}</th>
                <th>{t('users.colRole')}</th>
                <th>{t('users.colCreated')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((m) => (
                <>
                  <tr key={m.id}>
                    <td>{m.full_name ?? <span className="muted">{t('common.emptyDash')}</span>}</td>
                    <td>
                      <span className="muted" style={{ fontSize: 12 }}>
                        {m.email ?? t('common.emptyDash')}
                      </span>
                    </td>
                    <td>
                      <select
                        value={m.role}
                        disabled={roleChanging === m.id}
                        onChange={(e) =>
                          void changeRole(m.id, e.target.value as 'admin' | 'manager' | 'accountant')
                        }
                        style={{ fontSize: 12, padding: '3px 6px' }}
                      >
                        <option value="manager">{t('users.roleManagerOpt')}</option>
                        <option value="admin">{t('users.roleAdminOpt')}</option>
                        <option value="accountant">{t('users.roleAccountantOpt')}</option>
                      </select>
                    </td>
                    <td>
                      <span className="muted">{fmt(m.created_at)}</span>
                    </td>
                    <td>
                      <button
                        className="btn-ghost btn-sm"
                        disabled={resetBusy && resetId === m.id}
                        onClick={() => {
                          if (resetId === m.id) {
                            setResetId(null);
                            setResetCreds(null);
                          } else {
                            void resetPassword(m.id);
                          }
                        }}
                      >
                        {resetBusy && resetId === m.id
                          ? t('users.resetting')
                          : resetId === m.id
                            ? t('common.hide')
                            : t('users.resetPassword')}
                      </button>
                    </td>
                  </tr>
                  {resetId === m.id && resetCreds && (
                    <tr key={`${m.id}-creds`}>
                      <td
                        colSpan={5}
                        style={{ background: 'var(--accent-dim)', padding: '12px 16px' }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: 24,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            {t('users.resetCreds')}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <code style={{ fontSize: 12 }}>{resetCreds.email}</code>
                            <button
                              className="btn-ghost btn-sm"
                              onClick={() => copyReset(resetCreds.email, 'email')}
                            >
                              {resetCopied === 'email' ? t('users.copied') : t('users.copyEmail')}
                            </button>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <code style={{ fontSize: 12 }}>{resetCreds.password}</code>
                            <button
                              className="btn-ghost btn-sm"
                              onClick={() => copyReset(resetCreds.password, 'password')}
                            >
                              {resetCopied === 'password' ? t('users.copied') : t('users.copyPassword')}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
